'use client';

import LoadingCircular from '@/components/LoadingCircular';
import Dialog from '@/components/Dialog';
import Popup from '@/components/Popup';
import ProductCardMedia from '@/components/Product/ProductCardMedia';
import type { Product } from '@/components/Product/ProductCard';
import Wish from '@/components/Product/Wish';
import { EPlatform } from '@/utils/enums/platform-enum';
import SwiperNavigationButtons from '@/components/SwiperNavigation';
import { ListProduct } from '@/api/types/public';
import {
  SeedingDetailProduct,
  SeedingItemDetailResponse,
} from '@/app/(activity)/campaigns/[slug]/types';
import { normalizeSeedingComments } from '../utils/normalizeSeedingComment';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol, priceInfo } from '@/utils/price';
import { NewCloseOutlined } from '@zeelool/icon';
import {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  useContext,
} from 'react';
import type { Swiper as SwiperType } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { getSeedingMediaUrl } from '../utils/getSeedingMediaUrl';
import Button from '@/components/Button';
import { useRouter } from 'next/navigation';
import { colors, font } from '@/theme/zeelool.theme';
import { useTranslations } from '@/i18n/use-translations';
import styles from './SeedingItemDialog.module.css';
import appCommunicationManager from '@/utils/app-communication-manager';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';

function mapSeedingProductForCardMedia(product: SeedingDetailProduct): Product {
  return {
    ...product,
    name: product.name || product.short_name || '',
    is_glass: (product as Product).is_glass ?? 1,
    status: product.status ?? 1,
    stock: (product as Product).stock ?? 1,
    image: {
      product: product.image?.product ?? [],
      product_gray: (product.image as Product['image'])?.product_gray ?? [],
      celebrity: product.image?.celebrity ?? [],
      material: product.image?.material ?? [],
    },
  } as Product;
}

/** PC 种草弹窗设计稿尺寸（@1280px 视口为基准，更小视口等比缩小） */
const PC_DIALOG_WIDTH = 1241;
const PC_DIALOG_HEIGHT = 748;
const PC_RESPONSIVE_BREAKPOINT = 1280;
const WAP_MEDIA_HEIGHT = 375;

interface SeedingDetailMediaSwiperProps {
  materials: Array<{ id: number; path?: string }>;
  kolName: string;
  productDisplayName: string;
  isWap: boolean;
  resetKey: number;
}

/** 种草弹窗左侧媒体轮播（对齐商品详情 MainPicture 的 Swiper 用法，无大图预览） */
function SeedingDetailMediaSwiper({
  materials,
  kolName,
  productDisplayName,
  isWap,
  resetKey,
}: SeedingDetailMediaSwiperProps) {
  const swiperRef = useRef<SwiperType | null>(null);
  const showNav = materials.length > 1;
  const tSeeding = useTranslations('activity.campaigns.seeding');

  const handleAfterInit = useCallback(
    (swiper: SwiperType) => {
      swiperRef.current = swiper;
      swiper.updateSize();
      swiper.updateSlides();
      swiper.update();
      swiper.navigation?.init();
      swiper.navigation?.update();
      swiper.slideTo(0, 0);
      if (isWap) {
        swiper.updateAutoHeight();
      }
    },
    [isWap],
  );

  const handleSwiper = useCallback((swiper: SwiperType) => {
    swiperRef.current = swiper;
  }, []);

  const handleSlideChange = useCallback(() => {
    if (isWap) {
      swiperRef.current?.updateAutoHeight();
    }
  }, [isWap]);

  const handleImageLoad = useCallback(() => {
    if (isWap) {
      swiperRef.current?.updateAutoHeight();
    }
  }, [isWap]);

  useEffect(() => {
    const swiper = swiperRef.current;
    if (!swiper || swiper.destroyed) return;
    swiper.updateSize();
    swiper.updateSlides();
    swiper.update();
    swiper.slideTo(0, 0);
    swiper.navigation?.update();
    if (isWap) {
      swiper.updateAutoHeight();
    }
  }, [resetKey, isWap]);

  if (materials.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-[#eeeeee] text-[14px] font-ir text-auxiliary-dark-gray">
        {tSeeding('noImage')}
      </div>
    );
  }

  if (isWap) {
    return (
      <div
        className={`swiper-nav-container seeding-detail-media-swiper relative w-full overflow-hidden bg-[#eeeeee] ${styles.wapMediaSwiper}`}
        style={{ height: `${WAP_MEDIA_HEIGHT}px` }}
      >
        <Swiper
          modules={[Pagination]}
          className="h-full w-full"
          slidesPerView={1}
          pagination={
            showNav
              ? {
                  clickable: true,
                }
              : false
          }
          onSwiper={handleSwiper}
          onAfterInit={handleAfterInit}
          onSlideChange={handleSlideChange}
          style={
            {
              width: '100%',
              '--swiper-navigation-color': 'transparent',
            } as CSSProperties
          }
        >
          {materials.map((material, index) => (
            <SwiperSlide key={material.id} className="!h-full">
              <div className="relative h-full w-full overflow-hidden">
                <div className="relative h-full w-full">
                  <img
                    src={getSeedingMediaUrl(material.path)}
                    alt={kolName || productDisplayName || 'seeding detail'}
                    className="block h-full w-full select-none object-contain"
                    draggable={false}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                    onLoad={handleImageLoad}
                  />
                  {kolName && (
                    <div className="pointer-events-none absolute bottom-[12px] right-[10px] z-[2]">
                      <span className="inline-block max-w-[160px] truncate rounded-[100px] bg-white/40 px-[4px] h-[14px] leading-[14px] text-[10px] font-br text-primary-text backdrop-blur-sm">
                        @{kolName}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    );
  }

  return (
    <div className="swiper-nav-container seeding-detail-media-swiper relative h-full w-full min-w-0 max-w-full [&_.swiper-nav-button]:pointer-events-auto [&_.swiper-nav-button]:opacity-100">
      <div className="relative h-full w-full min-w-0 overflow-hidden">
        <Swiper
          modules={[Navigation]}
          className="h-full w-full min-w-0"
          pagination={false}
          navigation={
            showNav
              ? {
                  prevEl: '.seeding-detail-media-swiper-prev',
                  nextEl: '.seeding-detail-media-swiper-next',
                }
              : undefined
          }
          onSwiper={handleSwiper}
          onAfterInit={handleAfterInit}
          style={
            {
              width: '100%',
              height: '100%',
              '--swiper-navigation-color': 'transparent',
            } as CSSProperties
          }
        >
          {materials.map((material, index) => (
            <SwiperSlide key={material.id} className="!h-full">
              <div className="flex h-full w-full items-center justify-center overflow-hidden bg-[#eeeeee]">
                <div className="relative h-full w-full">
                  <img
                    src={getSeedingMediaUrl(material.path)}
                    alt={kolName || productDisplayName || 'seeding detail'}
                    className="block h-full w-full select-none object-contain"
                    draggable={false}
                    loading={index === 0 ? 'eager' : 'lazy'}
                    decoding="async"
                    fetchPriority={index === 0 ? 'high' : 'auto'}
                  />
                  {kolName && (
                    <div className="pointer-events-none absolute bottom-[12px] right-[12px] z-[2]">
                      <span className="inline-block max-w-[160px] truncate rounded-[100px] bg-white/60 px-[10px] py-[4px] text-[12px] font-br leading-[16px] text-primary-text backdrop-blur-sm">
                        @{kolName}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        {showNav && (
          <SwiperNavigationButtons
            navigationPrefix="seeding-detail-media-swiper"
            prevButtonPosition="left-[20px]"
            nextButtonPosition="right-[20px]"
            iconStyle={{ fontSize: '13px' }}
          />
        )}
      </div>
    </div>
  );
}

interface SeedingItemDialogProps {
  open: boolean;
  detail: SeedingItemDetailResponse | null;
  loading: boolean;
  onClose: () => void;
  isWap: boolean;
  index: number;
  trackCampaignPopupCollectClick: (item: SeedingDetailProduct, index: number) => void;
  trackCampaignPopupClick: (item: SeedingDetailProduct, index: number) => void;
}

function SeedingItemDialog({
  open,
  detail,
  loading,
  onClose,
  isWap,
  index,
  trackCampaignPopupCollectClick,
  trackCampaignPopupClick,
}: SeedingItemDialogProps) {
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const [swiperKey, setSwiperKey] = useState(0);

  const router = useRouter();
  const tCartDetail = useTranslations('cart.cartDetailPopup');
  const tSeeding = useTranslations('activity.campaigns.seeding');
  const seeding = detail?.seeding;
  const product = detail?.product;

  const materials = useMemo(() => {
    const list = seeding?.materials ?? [];
    return list
      .filter((item) => item.path && !item.is_expired)
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
  }, [seeding?.materials]);

  const featuredComments = useMemo(
    () => normalizeSeedingComments(seeding?.payload?.featured_comments),
    [seeding?.payload?.featured_comments],
  );
  const kolName = seeding?.payload?.kol_name ?? '';

  const productDisplayName = product?.short_name || product?.name || '';

  const productForCardMedia = useMemo(
    () => (product ? mapSeedingProductForCardMedia(product) : null),
    [product],
  );

  const { sellPrice, originalPrice } = useMemo(() => {
    if (!product) return { sellPrice: '', originalPrice: '' };
    const info = priceInfo(userInfo, product, currency);
    const sell = info.sell_price || product.price || '';
    const origin = info.price || '';
    const hasDiscount = origin && sell && origin !== sell;
    return {
      sellPrice: sell ? formatPriceNumberWithSymbol(String(sell), currency) : '',
      originalPrice: hasDiscount ? formatPriceNumberWithSymbol(String(origin), currency) : '',
    };
  }, [product, userInfo, currency]);

  useEffect(() => {
    if (open && detail) {
      setSwiperKey((key) => key + 1);
    }
  }, [open, detail, seeding?.item_id]);

  const { deviceType } = useContext(ResponsiveContext);
  const inAppH5 = [EPlatform.IOS, EPlatform.ANDROID].includes(deviceType);

  const onBuyNowClick = useCallback(() => {
    if (!product?.shop_sku) return;

    trackCampaignPopupClick(product, index);

    if (inAppH5) {
      appCommunicationManager.sendMessage(
        EAppCommunicationFuncName.JUMP_GOODS_DETAIL,
        product.shop_sku,
      );
    } else {
      router.push(`/goods-detail/${product.shop_sku}`);
    }
  }, [product, index, trackCampaignPopupClick, inAppH5, router]);

  const renderComments = (wapLayout = false) => {
    if (featuredComments.length === 0) {
      return (
        <p
          className={`font-ir text-auxiliary-dark-gray ${wapLayout ? 'text-[13px]' : 'text-[14px]'}`}
        >
          {tSeeding('noCommentsYet')}
        </p>
      );
    }

    return featuredComments.map((comment, index) => (
      <div
        key={`${index}-${comment.reviewer ?? ''}-${comment.content?.slice(0, 12)}`}
        className={`border-b border-auxiliary-light-gray last:border-b-0 last:pb-0 first:pt-0 ${
          wapLayout ? 'py-[16px]' : 'py-[20px]'
        }`}
      >
        {comment.reviewer && (
          <p
            className={`font-ir text-auxiliary-dark-gray ${
              wapLayout
                ? 'mb-[6px] text-[12px] leading-[16px]'
                : 'mb-[8px] text-[13px] leading-[17px]'
            }`}
          >
            {comment.reviewer}
          </p>
        )}
        <p
          className={`font-br leading-[20px] text-primary-text line-clamp-5 ${
            wapLayout ? 'text-[13px]' : 'text-[14px]'
          }`}
        >
          {comment.content}
        </p>
      </div>
    ));
  };

  const renderProductCard = (variant: 'pc' | 'wap' = 'pc') => {
    if (!product?.shop_sku) return null;

    const isWapCard = variant === 'wap';

    return (
      <div
        className={`relative flex items-center gap-[10px] bg-auxiliary-light-gray ${isWapCard ? 'rounded-[8px]' : 'rounded-[12px]'}`}
      >
        {product && (
          <div
            className={`absolute right-[0] top-[0] z-[1] flex items-center justify-center rounded-bl-[12px] bg-white/70 ${
              isWapCard ? 'h-[28px] w-[28px]' : 'h-[32px] w-[32px]'
            }`}
          >
            <Wish
              data={product as ListProduct}
              iconSize={isWapCard ? '18px' : '20px'}
              className="text-white"
              collectScene="种草活动专题页弹窗"
              price={sellPrice}
              onCollectClick={() => trackCampaignPopupCollectClick(product, index)}
            />
          </div>
        )}
        {productForCardMedia && (
          <div
            className={`shrink-0 overflow-hidden rounded-[8px] ${
              isWapCard ? 'h-[140px] w-[140px]' : 'h-[130px] w-[130px]'
            }`}
          >
            <ProductCardMedia
              product={productForCardMedia}
              deviceType={isWapCard ? EPlatform.WAP : EPlatform.WEB}
              showWish={false}
              showTag={false}
              isSquare
              className="!aspect-square h-full rounded-[8px]"
              frameWidth="80%"
              SoldOutBlock={null}
              TryOnButton={null}
            />
          </div>
        )}
        <div className="flex min-w-0 flex-1 gap-[12px]">
          <div className={`min-w-0 flex-1 ${isWapCard ? 'pr-[24px]' : 'pr-[12px]'}`}>
            <p
              className={`font-semibold text-primary-text line-clamp-2 ${
                isWapCard ? 'text-[16px] leading-[20px]' : 'text-[20px]'
              }`}
            >
              {productDisplayName}
            </p>
            <p className={`text-primary-text ${isWapCard ? 'text-[12px]' : 'text-[14px]'}`}>
              {tSeeding('color')}: {product?.product_frame_color_name || ''}
            </p>
            <div
              className={`flex items-center justify-between ${isWapCard ? 'mt-[8px] flex-col gap-[12px] items-start' : 'mt-[16px]'}`}
            >
              <div
                className={`flex flex-wrap items-baseline ${isWapCard ? 'flex-row gap-[7px]' : 'flex-col gap-[2px]'}`}
              >
                {sellPrice && (
                  <p
                    className={`font-bb text-primary-text ${
                      isWapCard ? 'text-[20px] leading-[22px]' : 'text-[24px]'
                    }`}
                  >
                    {sellPrice}
                  </p>
                )}
                {originalPrice && (
                  <p
                    className={`font-ir text-auxiliary-dark-gray line-through ${
                      isWapCard ? 'text-[14px]' : 'text-[16px]'
                    }`}
                  >
                    {originalPrice}
                  </p>
                )}
              </div>
              <Button
                onClick={onBuyNowClick}
                sx={{
                  height: isWapCard ? '32px' : '40px',
                  width: 'auto',
                  minWidth: 0,
                  px: isWapCard ? '16px' : '14px',
                  borderRadius: isWapCard ? '16px' : '20px',
                  backgroundColor: colors.primaryPurple,
                  fontSize: isWapCard ? '14px' : '16px',
                  fontFamily: font.bb,
                  whiteSpace: 'nowrap',
                  color: 'white',
                  transition: 'opacity 0.3s',
                  '&:hover': {
                    opacity: 0.9,
                  },
                }}
              >
                {tSeeding('buyNow')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderWapDialogBody = () => (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className={`${styles.wapScroll} flex min-h-0 flex-1 flex-col`}>
        <div className="">
          <SeedingDetailMediaSwiper
            materials={materials}
            kolName={kolName}
            productDisplayName={productDisplayName}
            isWap
            resetKey={swiperKey}
          />
        </div>
        <div className="px-[16px] py-[16px]">{renderComments(true)}</div>
        {product?.shop_sku && (
          <div className={styles.wapStickyProduct}>{renderProductCard('wap')}</div>
        )}
      </div>
    </div>
  );

  const renderPcDialogBody = () => (
    <div className={`${styles.pcDialogBody} relative flex h-full min-h-0 w-full flex-1`}>
      <button
        type="button"
        aria-label="Close"
        className="absolute right-[12px] top-[12px] z-[2] flex h-[32px] w-[32px] items-center justify-center rounded-full text-primary-text cursor-pointer"
        onClick={onClose}
      >
        <NewCloseOutlined className="text-[20px]" />
      </button>
      <div className={`${styles.pcMediaPanel} relative shrink-0 overflow-hidden bg-[#eeeeee]`}>
        <SeedingDetailMediaSwiper
          materials={materials}
          kolName={kolName}
          productDisplayName={productDisplayName}
          isWap={false}
          resetKey={swiperKey}
        />
      </div>

      <div
        className="relative box-border flex min-h-0 shrink-0 flex-col overflow-hidden bg-white"
        style={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <div className="flex min-h-0 flex-1 flex-col pt-[32px]">
          <div className={`${styles.pcScroll} min-h-0 flex-1 overflow-y-auto`}>
            <div className="px-[24px]">{renderComments()}</div>
            {product?.shop_sku && (
              <div className={`${styles.pcStickyProduct} mx-[24px]`}>{renderProductCard('pc')}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const wapPopupSx = {
    '& .MuiDrawer-paper': {
      height: '100dvh',
      maxHeight: '100dvh',
      borderRadius: 0,
      overflow: 'hidden',
    },
    '& .MuiDrawer-paper > .MuiBox-root': {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      overflow: 'hidden',
    },
    '& .popup-title': {
      flexShrink: 0,
      position: 'sticky',
      top: 0,
      zIndex: 2,
      backgroundColor: colors.primaryWhite,
    },
    '& .popup-content.seeding-wap-popup-body': {
      padding: '0 !important',
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      minHeight: 0,
      overflow: 'hidden',
    },
  } as const;

  if (!open) return null;

  if (isWap) {
    return (
      <Popup
        open={open}
        position="bottom"
        height="100dvh"
        round={false}
        borderRadius={0}
        title={tCartDetail('details')}
        onClose={onClose}
        closeOnClickOverlay
        overlay
        bodyClassName="seeding-wap-popup-body"
        sx={wapPopupSx}
      >
        {loading ? (
          <div className="flex min-h-[240px] flex-1 items-center justify-center">
            <LoadingCircular />
          </div>
        ) : (
          detail && renderWapDialogBody()
        )}
      </Popup>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      hideHeader
      showCloseIcon={false}
      footer={null}
      closeOnClickOverlay
      ariaLabel="Seeding item detail"
      maxWidth={false}
      sx={{
        '& .MuiDialog-paper': {
          width: `${PC_DIALOG_WIDTH}px`,
          maxWidth: 'calc(100vw - 39px)',
          height: `${PC_DIALOG_HEIGHT}px`,
          maxHeight: `min(90vh, calc((100vw - 39px) * ${PC_DIALOG_HEIGHT} / ${PC_DIALOG_WIDTH}))`,
          margin: '16px',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
        },
        '@media (max-width: 1279px)': {
          '& .MuiDialog-paper': {
            width: `calc((100vw - 39px) * ${PC_DIALOG_WIDTH} / ${PC_RESPONSIVE_BREAKPOINT})`,
            height: `min(calc((100vw - 39px) * ${PC_DIALOG_HEIGHT} / ${PC_RESPONSIVE_BREAKPOINT}), 90vh)`,
            maxHeight: '90vh',
          },
        },
        '@media (max-width: 1079px)': {
          '& .MuiDialog-paper': {
            height: '90vh',
            maxHeight: '90vh',
          },
        },
        '& .MuiDialogContent-root': {
          padding: '0 !important',
          display: 'flex',
          flex: 1,
          minHeight: 0,
          maxHeight: 'none !important',
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
        },
        '& .MuiDialogContent-root > div': {
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        },
      }}
      contentProps={{
        className: 'flex h-full min-h-0 flex-1 overflow-hidden p-0',
      }}
    >
      {loading ? (
        <div className="flex h-full w-full items-center justify-center">
          <LoadingCircular />
        </div>
      ) : (
        detail && renderPcDialogBody()
      )}
    </Dialog>
  );
}

export default memo(SeedingItemDialog);
